import "@mantine/core/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/notifications/styles.css";
import "./styles/app.css";

import { ColorSchemeScript, MantineProvider, localStorageColorSchemeManager } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { AuthProvider } from "./auth";
import { stampBuildInfo } from "./lib/buildInfo";
import { DEMO_MODE, markMswWorkerStarted, mswActive } from "./lib/mswEnv";
import { createQueryClient } from "./lib/queryClient";
import { theme } from "./theme";

// Demo builds only: a phone-sized viewport gets a "use a laptop or tablet"
// notice instead of the app. `DEMO_MODE` is a build-time constant, so every
// other bundle drops the chunk.
const DemoMobileGate = DEMO_MODE ? lazy(() => import("./demo/DemoMobileGate")) : null;

const queryClient = createQueryClient();

// Persist the user's light/dark choice (set from the profile dropdown) across
// reloads. ColorSchemeScript restores it before first paint to avoid a flash.
const colorSchemeManager = localStorageColorSchemeManager({ key: "compliance-color-scheme" });

// MSW: intercept fetch() so components run against the same wire contract
// the real backend will serve.
//
// The risk we're protecting against: a production build that silently
// mocks write paths (uploads, report generation) while real
// auth + batches load. A reviewer could "approve" against a mock and
// never know. So MSW is gated on TWO axes (see `lib/mswEnv.ts`, the shared
// source of truth so UI gates can't diverge from the worker decision):
//
//   - **Production builds** (`import.meta.env.PROD`): MSW is opt-IN
//     and requires `VITE_USE_MSW=true` AND a localhost API base URL.
//     This is the defense-in-depth path; a misconfigured staging deploy
//     can't accidentally serve mocks.
//   - **Dev builds** (`pnpm dev`): MSW is opt-OUT — defaults to ON for
//     DX (the team relies on `git clone && pnpm dev` working out of the
//     box). Set `VITE_USE_MSW=false` in `.env.local` to disable.
const USE_MSW = mswActive();

async function bootstrap() {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element #root not found in index.html");
  }

  if (USE_MSW) {
    try {
      const { startMsw } = await import("./mocks/browser");
      await startMsw();
      // Record that the worker actually intercepts, so mock-only UI (the demo
      // uploads panel) gates on real startup — not just on the env intent.
      markMswWorkerStarted();
    } catch (err) {
      if (DEMO_MODE) {
        // Demo mode has no backend behind the worker. Rendering the app against
        // a dead worker would show a wall of network errors that reads like a
        // real outage, so fail closed with a screen that says what happened.
        const { renderDemoBootFailure } = await import("./demo/DemoBootFailure");
        renderDemoBootFailure(rootElement, err);
        return;
      }
      // Mock layer failed — render anyway so the user can sign in / see
      // the error in the network tab, rather than the page going blank.
      // eslint-disable-next-line no-console
      console.error("[MSW] failed to start — backend requests will hit Vite SPA fallback:", err);
    }
  }

  // Before the first render, so a bundle that then fails to boot is still
  // identifiable from devtools.
  stampBuildInfo();

  createRoot(rootElement).render(
    <StrictMode>
      <ColorSchemeScript defaultColorScheme="light" />
      <MantineProvider
        theme={theme}
        defaultColorScheme="light"
        colorSchemeManager={colorSchemeManager}
      >
        <Notifications position="top-right" />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            {DemoMobileGate ? (
              <Suspense fallback={null}>
                <DemoMobileGate>
                  <App />
                </DemoMobileGate>
              </Suspense>
            ) : (
              <App />
            )}
          </AuthProvider>
          {import.meta.env.DEV ? (
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
          ) : null}
        </QueryClientProvider>
      </MantineProvider>
    </StrictMode>,
  );
}

void bootstrap();
